import { useMemo } from 'react';
import { TenantCountryCodeEnum } from '../../api';
import { useTenant } from '../me';

export const useStaticLinks = () => {
  const { tenant } = useTenant();

  const staticLinks = useMemo(() => {
    return tenant ? PROBONIO_LINK_CONFIG[tenant.countryCode] : null;
  }, [tenant]);

  return staticLinks;
};

/**
 * Derives the country code from the current domain, if tenant is not available.
 * If the country code cannot be determined, it defaults to DE.
 */
export const getDomainCountryCode = (): TenantCountryCodeEnum => {
  const tld = window?.location.hostname.split('.').pop()?.toUpperCase();
  if (tld && Object.values(TenantCountryCodeEnum).includes(tld as TenantCountryCodeEnum)) {
    return tld as TenantCountryCodeEnum;
  }
  return TenantCountryCodeEnum.De; // default to DE if not found
};

export type StaticLinks = {
  // Probonio links
  IMPRINT: string;
  PRIVACY: string;
  MAIL: string;
  MANUAL: string;
  /**
   * TEL will no longer be used after oneportal golive.
   * Then the multiple TEL_* fields will be used instead.
   *
   * Note that the old number is not used in the oneportal TEL_* fields anymore.
   */
  TEL: string;
  TEST: string;
  WA?: string;
  GDPR?: string;
  APP_PRIVACY?: string;
  HUBSPOT_CALENDAR_LINK?: string;
  BLS_WEB: string;
  BLS_TEL: string;
  BLS_AG: string;
  BLS_INFO: string;
  BLS_PORTAL_INT: string;
  BLS_PORTAL_USER: string;
  BLS_PORTAL_REG: string;

  // New fields for feature oneportal
  MAIL_ACCOUNTING?: string;
  TEL_GENERAL_SUPPORT: string;
  TEL_BENEFIT_SUPPORT: string;
  TEL_ACCOUNTING?: string;
  TEL_SALES?: string;
  MAIL_SALES?: string;
  BLS_EMPLOYER_INSURANCE_TEL: string;
  BLS_EMPLOYER_INSURANCE_MAIL: string;
  BLS_BIKE_INSURANCE_TEL: string;
  BLS_BIKE_INSURANCE_MAIL: string;
  BLS_OTHER_TOPICS_TEL: string;
  BLS_OTHER_TOPICS_MAIL: string;
  BLS_BUYOUT_TEL: string;
  BLS_BUYOUT_MAIL: string;
  BLS_DEALER_SUPPORT_TEL: string;
  BLS_DEALER_SUPPORT_MAIL: string;
  BLS_LOGISTICS_TEL: string;
  BLS_LOGISTICS_MAIL: string;
  BLS_WA: string;
  BLS_PRIVACY: string;
  BLS_DOI_DETAILS: string;
};
export const PROBONIO_LINK_CONFIG: Record<TenantCountryCodeEnum, StaticLinks> = {
  [TenantCountryCodeEnum.De]: {
    IMPRINT: 'https://www.probonio.de/impressum',
    PRIVACY: 'https://www.probonio.de/datenschutz',
    MAIL: 'support@probonio.de',
    MANUAL: 'https://support.probonio.de/hc',
    TEL: '+49 871 97623482',
    WA: '+49 163 3666523',
    HUBSPOT_CALENDAR_LINK: 'https://meetings-eu1.hubspot.com/pia-fichtinger/probonio-onboarding',
    GDPR: 'https://www.probonio.de/resources/PBL-Landshut-IP-all.pdf',
    APP_PRIVACY: 'https://probonio.de/datenschutz-app',
    TEST: 'https://www.probonio.de/kostenlos-testen',

    BLS_TEL: '+49 55 71 / 30 26 - 0',
    BLS_INFO: 'info@bikeleasing.de',
    BLS_WEB: 'https://bikeleasing.de/',
    BLS_AG: 'https://bikeleasing.de/',
    BLS_PORTAL_INT: 'https://portal.bikeleasing.de/user/integrations/probonio/create',
    BLS_PORTAL_USER: 'https://portal.bikeleasing.de/user/profile',
    BLS_PORTAL_REG: 'https://portal.bikeleasing.de/user/profile',

    MAIL_ACCOUNTING: 'buchhaltung@probonio.de',
    TEL_GENERAL_SUPPORT: '+49 561 94039143',
    TEL_BENEFIT_SUPPORT: '+49 561 94039143',
    TEL_ACCOUNTING: '+49 871 97623390',
    TEL_SALES: '+49 871 97622993',
    MAIL_SALES: 'sales@probonio.de',
    BLS_EMPLOYER_INSURANCE_TEL: '+49 5571 / 3026 - 205',
    BLS_EMPLOYER_INSURANCE_MAIL: 'agv@bikeleasing.de',
    BLS_BIKE_INSURANCE_TEL: '+49 5571 / 3026 - 200',
    BLS_BIKE_INSURANCE_MAIL: 'bikeschaden@bikeleasing.de',
    BLS_OTHER_TOPICS_TEL: '+49 5571 / 3026 - 206',
    BLS_OTHER_TOPICS_MAIL: 'info@bikeleasing.de',
    BLS_BUYOUT_TEL: '+49 5571 / 3026 - 209',
    BLS_BUYOUT_MAIL: 'abloese@bikeleasing.de',
    BLS_DEALER_SUPPORT_TEL: '+49 5571 / 3026 - 118',
    BLS_DEALER_SUPPORT_MAIL: 'fachhandel@bikeleasing.de',
    BLS_LOGISTICS_TEL: '+49 5571 / 3026 - 208',
    BLS_LOGISTICS_MAIL: 'logistik@bikeleasing.de',
    BLS_WA: '+49 171 2191384',
    BLS_PRIVACY: 'https://files.bikeleasing.de/pdf/de/dsgvo-bikeleasing-de.pdf',
    BLS_DOI_DETAILS: 'https://bikeleasing.de/doi-details',
  },

  [TenantCountryCodeEnum.At]: {
    IMPRINT: 'https://www.probonio.at/impressum',
    PRIVACY: 'https://www.probonio.at/datenschutz',
    MAIL: 'support@probonio.at',
    MANUAL: 'https://support.probonio.at/hc',
    APP_PRIVACY: 'https://probonio.at/datenschutz-app',
    TEL: '+43 512 219321 70',
    TEST: 'https://www.probonio.at/kostenlos-testen',

    BLS_WEB: 'https://bikeleasing.at/',
    BLS_INFO: 'info@bikeleasing.at',
    BLS_TEL: '+43 512 / 219 32 - 170',
    BLS_AG: 'https://bikeleasing.at/',
    BLS_PORTAL_INT: 'https://portal.bikeleasing.at/user/integrations/probonio/create',
    BLS_PORTAL_USER: 'https://portal.bikeleasing.at/user/profile',
    BLS_PORTAL_REG: 'https://portal.bikeleasing.at/user/profile',

    TEL_GENERAL_SUPPORT: '+43 512 387 191',
    TEL_BENEFIT_SUPPORT: '+43 512 219321 70',
    BLS_EMPLOYER_INSURANCE_TEL: '+43 512 / 219 32 - 195',
    BLS_EMPLOYER_INSURANCE_MAIL: 'agv@bikeleasing.at',
    BLS_BIKE_INSURANCE_TEL: '+43 512 / 219 32 - 190',
    BLS_BIKE_INSURANCE_MAIL: 'bikeschaden@bikeleasing.at',
    BLS_OTHER_TOPICS_TEL: '+43 512 / 219 32 - 100',
    BLS_OTHER_TOPICS_MAIL: 'info@bikeleasing.at',
    BLS_BUYOUT_TEL: '+43 512 / 219 32 - 199',
    BLS_BUYOUT_MAIL: 'abloese@bikeleasing.at',
    BLS_DEALER_SUPPORT_TEL: '+43 512 / 219 32 - 1100',
    BLS_DEALER_SUPPORT_MAIL: 'fachhandel@bikeleasing.at',
    BLS_LOGISTICS_TEL: '+43 512 / 219 32 - 198',
    BLS_LOGISTICS_MAIL: 'logistik@bikeleasing.de',
    BLS_WA: '+43 676 4140360',
    BLS_PRIVACY: 'https://files.bikeleasing.de/pdf/at/dsgvo-bikeleasing-at.pdf',
    BLS_DOI_DETAILS: 'https://bikeleasing.at/doi-details',
  },
};
